import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { type UseFormReturn } from "react-hook-form";
import { type FormSchema, focusOptions } from "../onboarding-schema";
import { Info, SearchIcon } from "lucide-react";

interface FocusStepProps {
  form: UseFormReturn<FormSchema>;
  showOtherFocus: boolean;
}

export function FocusStep({ form, showOtherFocus }: FocusStepProps) {
  // Helper function to toggle an option in the array
  const toggleOption = (currentValues: string[], optionId: string) => {
    if (currentValues.includes(optionId)) {
      return currentValues.filter(value => value !== optionId);
    } else {
      return [...currentValues, optionId];
    }
  };

  return (
    <>
      <FormLabel className="text-xl md:text-2xl font-semibold block mb-3 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
        What is your primary focus?
      </FormLabel>
      <div className="flex items-center mb-3 gap-1.5">
        <Info className="h-3.5 w-3.5 text-blue-400" />
        <p className="text-xs text-blue-100/60">Select all that apply to you</p>
      </div>
      <FormField
        control={form.control}
        name="primary_focus"
        render={({ field }) => {
          const values = Array.isArray(field.value) ? field.value : [];
          
          return (
            <FormItem className="space-y-3">
              <FormControl>
                <div className="space-y-3">
                  {focusOptions.map((option) => {
                    const isSelected = values.includes(option.id);
                    
                    return (
                      <label 
                        key={option.id}
                        className={`flex items-center p-4 md:p-4 rounded-lg border cursor-pointer transition-all duration-200 ${
                          isSelected
                            ? "border-blue-400/50 bg-blue-500/10 shadow-md"
                            : "border-gray-700/80 hover:border-gray-600/80 hover:bg-gray-800/50 bg-gray-900/40"
                        }`}
                      >
                        <Checkbox
                          checked={isSelected}
                          className={`mr-3 ${isSelected ? "border-blue-400 text-blue-400" : "text-gray-500 border-gray-600"}`}
                          onCheckedChange={() => {
                            field.onChange(toggleOption(values, option.id));
                          }}
                        />
                        <span className={`text-sm font-medium flex-grow ${isSelected ? "text-blue-50" : "text-gray-300"}`}>
                          {option.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </FormControl>
              <FormMessage className="text-sm text-red-400" />
            </FormItem>
          );
        }}
      />
      {showOtherFocus && (
        <FormField
          control={form.control}
          name="other_focus"
          render={({ field }) => (
            <FormItem className="mt-5">
              <FormLabel className="text-sm font-medium text-blue-100/80 mb-2 block">Please specify your focus area</FormLabel>
              <FormControl>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <SearchIcon className="h-4 w-4 text-blue-400" />
                  </div>
                  <Input
                    placeholder="Enter your specific market focus"
                    className="pl-10 bg-gray-800/50 border-gray-700 h-12 focus:border-blue-500 focus:ring-blue-500/20 transition-all duration-200"
                    {...field}
                    autoFocus
                  />
                </div>
              </FormControl>
              <FormMessage className="text-sm text-red-400" />
            </FormItem>
          )}
        />
      )}
    </>
  );
}